import { useEffect, useSyncExternalStore } from 'react';

import type { AuthUser, FeatureKey } from '@/api/auth';
import {
  AI_SKILLS_CLOSED,
  dropAiSkillsOfOtherUser,
  ensureAiSkills,
  getAiSkills,
  getAiSkillsVersion,
  subscribeAiSkills,
  type AiSkills,
} from '@/lib/aiSkills';
import { computeHasFeature, useAuthStore } from '@/stores/auth';

/** Ключи тарифа, при которых ученику вообще есть что спросить у ручки. */
const AI_TARIFF_KEYS: FeatureKey[] = ['ielts_reading', 'ielts_listening', 'ielts_writing', 'ielts_speaking', 'mock', 'sprint'];

/**
 * Нужна ли этому человеку ручка навыков.
 *
 * ⚠️ СТРОГО (`computeHasFeature(…, true)`): набора возможностей нет — запроса
 * нет, на сайте тоже. Бесплатнику (`lessons, vocab, cefr, ielts`) и ученику без
 * набора ни одного лишнего запроса (решение техлида 13.10.2026, сторож — в
 * `lib/aiSkills.test.ts`). Сотрудник проходит, как везде.
 */
export function aiSkillsWanted(user: AuthUser | null | undefined): boolean {
  if (!user) return false;
  return AI_TARIFF_KEYS.some((k) => computeHasFeature(user, k, true));
}

/**
 * Тумблеры ИИ-заданий ученика (docs/CONTRACTS/ai-skills.md) с перерисовкой по
 * приезде ответа.
 *
 * `enabled` — явное решение вызывающего (вкладка приложения передаёт его сама);
 * без аргумента — по тарифу (`aiSkillsWanted`). Не нужна — запроса нет, ответ
 * «всё закрыто».
 *
 * ⚠️ Нужен ЛЮБОМУ экрану, который показывает плитки или ссылки ИИ-заданий
 * (`withoutClosed`, `entryHidden`, `aiTrainersShown` в `lib/aiEntries.ts`):
 * ответ приходит после первой отрисовки, и без подписки экран остался бы
 * «всё закрыто» до следующей навигации.
 */
export function useAiSkills(enabled?: boolean): AiSkills {
  const user = useAuthStore((s) => s.user);
  const want = enabled ?? aiSkillsWanted(user);
  const userId = user?.id ?? null;
  // Не нужна — запроса нет, но ответ ДРУГОЙ учётки сбрасывается: иначе `aiEntries`
  // (читает хранилище напрямую) показал бы бесплатнику чужие навыки.
  // ⚠️ Ответ СВОЕЙ учётки не трогаем (найдено терминалом приложения 13.10.2026): до
  // этого здесь был ensureAiSkills(null), и вызов с enabled=false — оболочка, чей
  // эффект идёт после эффекта страницы, — выбрасывал ответ, запрошенный хабом или
  // главной той же учётки, и плитки оставались закрытыми до смены входа.
  useEffect(() => {
    if (want) void ensureAiSkills(userId);
    else dropAiSkillsOfOtherUser(userId);
  }, [want, userId]);
  useSyncExternalStore(subscribeAiSkills, getAiSkillsVersion, getAiSkillsVersion);
  return want ? getAiSkills() : AI_SKILLS_CLOSED;
}
